import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { demoShifts } from '@/mocks/demoData';

export default function Topbar() {
  const { user, logout, changePassword } = useAuth();
  const { sidebarOpen, toggleSidebar, addToast } = useApp();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const activeShift = demoShifts.find((s) => s.status === 'active');

  const currentTime = new Date();
  const timeStr = currentTime.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const dateStr = currentTime.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const handlePasswordChange = () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      addToast('error', 'All password fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      addToast('error', 'New password and confirm password do not match');
      return;
    }
    if (newPassword.length < 4) {
      addToast('error', 'Password must be at least 4 characters');
      return;
    }
    const success = changePassword(oldPassword, newPassword);
    if (success) {
      addToast('success', 'Password changed successfully');
      setShowPasswordModal(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      addToast('error', 'Current password is incorrect');
    }
  };

  return (
    <>
      <header className="h-16 bg-white border-b border-navy-100 flex items-center justify-between px-3 sm:px-4 lg:px-6 flex-shrink-0">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={toggleSidebar}
            className="w-9 h-9 rounded-lg border border-navy-200 flex items-center justify-center text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer"
          >
            <i className={sidebarOpen ? 'ri-menu-fold-line' : 'ri-menu-unfold-line'} />
          </button>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 lg:gap-5">
          {/* Date/Time */}
          <div className="hidden md:flex flex-col items-end text-xs text-navy-500">
            <span className="font-semibold text-navy-700">{timeStr}</span>
            <span>{dateStr}</span>
          </div>

          {/* Shift badge */}
          {activeShift && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-50 border border-gold-200">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-medium text-gold-700">
                {activeShift.name} (2PM - 10PM)
              </span>
            </div>
          )}

          {/* Notifications */}
          <button className="relative w-9 h-9 rounded-lg border border-navy-200 flex items-center justify-center text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">
            <i className="ri-notification-3-line" />
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-semibold">
              3
            </span>
          </button>

          {/* User */}
          <div className="relative flex items-center gap-2 sm:gap-3 pl-2 sm:pl-3 border-l border-navy-200">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 sm:gap-3 hover:bg-navy-50 rounded-lg px-2 py-1 transition-colors cursor-pointer"
            >
              <div className="text-right hidden sm:block">
                <div className="text-sm font-medium text-navy-800">{user?.full_name}</div>
                <div className="text-xs text-navy-400 capitalize">{user?.role}</div>
              </div>
              <div className="w-9 h-9 rounded-full bg-navy-700 text-white flex items-center justify-center text-sm font-semibold">
                {user?.full_name
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('')}
              </div>
              <i className="ri-arrow-down-s-line text-navy-400 text-sm hidden sm:block" />
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-navy-100 shadow-lg z-20 py-1.5">
                  <div className="px-4 py-2 border-b border-navy-100">
                    <div className="text-sm font-medium text-navy-800">{user?.full_name}</div>
                    <div className="text-xs text-navy-400">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowPasswordModal(true);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-lock-line" />
                    Change Password
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-logout-box-r-line" />
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-navy-800">Change Password</h3>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setOldPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="w-8 h-8 rounded-lg hover:bg-navy-50 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-navy-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Current Password</label>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="Confirm new password"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setOldPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordChange}
                  className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer"
                >
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}